/**
 * Ponto de Entrada da Aplicação React
 *
 * Este arquivo inicializa a aplicação React e configura
 * os providers globais necessários.
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider } from '@contexts/ThemeContext';
import { AuthProvider } from '@contexts/AuthContext';
import { SidebarProvider } from '@contexts/SidebarContext';
import { WebSocketProvider } from '@hooks/useWebSocket';
import { ErrorBoundary } from '@components/ui/ErrorBoundary';
import { PWAProvider } from '@components/pwa/PWAProvider';
import App from './App';
import { applySiteTheme, readStoredSiteTheme } from '@pages/Website/useSiteTheme';
import { setupThemeFavicon } from '@utils/themeFavicon';
import { installNumericInputGuard } from '@utils/numericInputGuard';
import { startInstallPromptCapture } from '@utils/pwaInstall';

// Importa estilos globais
import './styles/globals.css';

// Tema do site institucional antes da primeira renderização (evita piscar o tema errado)
applySiteTheme(readStoredSiteTheme());

// Favicon branco no tema escuro do navegador e roxo no claro
setupThemeFavicon();

// Campos numéricos sem "e", "+", sinal ou decimal indevidos (#288)
installNumericInputGuard();
// Instalação do app (#291): o pedido de instalação do navegador é guardado e nunca
// aparece sozinho. O link do manifesto só entra no painel e no convite pós-pedido da
// loja (@utils/appManifest)
startInstallPromptCapture();

/**
 * Renderiza a aplicação no DOM
 *
 * Estrutura de providers:
 * - BrowserRouter: Roteamento SPA
 * - ThemeProvider: Gerenciamento de tema claro/escuro
 * - AuthProvider: Estado de autenticação
 * - SidebarProvider: Estado da sidebar
 */
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <WebSocketProvider>
              <SidebarProvider>
                <PWAProvider>
                  {/* Aplicação principal */}
                  <App />

                  {/* Toast notifications */}
                  <Toaster
                    position="top-right"
                    toastOptions={{
                      // Estilos padrão
                      className: '',
                      duration: 4000,
                      style: {
                        background: '#1f2937',
                        color: '#f9fafb',
                        borderRadius: '8px',
                      },
                      // Estilos por tipo
                      success: {
                        iconTheme: {
                          primary: '#10b981',
                          secondary: '#f9fafb',
                        },
                      },
                      error: {
                        iconTheme: {
                          primary: '#ef4444',
                          secondary: '#f9fafb',
                        },
                      },
                    }}
                  />
                </PWAProvider>
              </SidebarProvider>
            </WebSocketProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>,
);
